import { useCallback } from "react";
import { Navigate, NavigateOptions, useLocation, useNavigate } from "react-router-dom";
import { applyStickyParams, NavigateGlobalQuery } from "./queries";

/**
 * Closed navigation API. Outside src/helpers/navigation/, these (and
 * pageContext.goTo) are the only sanctioned ways to navigate: they re-apply
 * the sticky queries (settings, slave, screen…) on every target, which raw
 * useNavigate / <Navigate> silently drop (enforced by no-restricted-imports).
 */

export interface AppNavigateOptions extends NavigateOptions {
  /**
   * Catalogued page queries (NavigateGlobalQuery) of the CURRENT url to carry
   * onto the target, on top of the always-carried sticky queries. Queries
   * already present on the target win.
   */
  forwardParams?: NavigateGlobalQuery[];
}

export type AppNavigateFunction = {
  (to: string, options?: AppNavigateOptions): void;
  (delta: number): void;
};

export const useAppNavigate = (): AppNavigateFunction => {
  const navigate = useNavigate();
  const appNavigate = useCallback((to: string | number, options?: AppNavigateOptions) => {
    if (typeof to === "number") {
      navigate(to);
      return;
    }
    const { forwardParams, ...navigateOptions } = options ?? {};
    // window.location is always current, even when the callback outlives the
    // render it was created in (timeouts, event listeners).
    navigate(applyStickyParams(to, window.location.search, forwardParams), navigateOptions);
  }, [navigate]);
  return appNavigate as AppNavigateFunction;
};

export interface AppNavigateProps {
  to: string;
  replace?: boolean;
  state?: any;
  /** Same as AppNavigateOptions.forwardParams. */
  forwardParams?: NavigateGlobalQuery[];
}

export const AppNavigate = ({ to, replace, state, forwardParams }: AppNavigateProps) => {
  const { search } = useLocation();
  return <Navigate to={applyStickyParams(to, search, forwardParams)} replace={replace} state={state} />;
};
